import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Calendar as CalendarIcon,
  UserCheck,
  Clock,
  Download,
  TrendingUp,
  MapPin,
  Target,
  PartyPopper,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Save,
  X,
  FileText,
  DollarSign,
  BarChart3,
} from "lucide-react";
import { getAllActiveUsers } from "@/services/userService";
import { getAllAttendanceForDateRange } from "@/services/attendanceService";
import { getActiveOffices } from "@/services/officeService";
import { db } from "@/lib/firebase";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import {
  getDailyCommitments,
  getAllMonthlyCommitments,
  saveDailyCommitment,
  saveMonthlyCommitment,
  DailyCommitment,
  MonthlyCommitment,
} from "@/services/commitmentService";
import { ApprovedUser } from "@/types";
import { AttendanceRecord } from "@/types/attendance";
import { Office } from "@/types/office";
import { isHoliday } from "@/lib/holidayUtils";
import { formatDistance as formatDistanceMeters } from "@/lib/locationUtils";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  addMonths,
  subMonths,
  isBefore,
  startOfDay,
} from "date-fns";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import * as XLSX from "xlsx";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type DayStatus = "present" | "absent" | "late" | "away" | "late_away" | "holiday" | "pending";

interface DayRecord {
  date: string;
  status: DayStatus;
  attendance?: AttendanceRecord;
  dailyCommitment?: DailyCommitment;
  holidayReason?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const STATUS_STYLES: Record<DayStatus, { bg: string; text: string; border: string; badge: string; label: string }> = {
  present:   { bg: "bg-green-50",  text: "text-green-800",  border: "border-green-200", badge: "bg-green-500",    label: "Present" },
  late:      { bg: "bg-yellow-50", text: "text-yellow-800", border: "border-yellow-200", badge: "bg-yellow-500",  label: "Late" },
  away:      { bg: "bg-orange-50", text: "text-orange-800", border: "border-orange-200", badge: "bg-orange-500",  label: "Away" },
  late_away: { bg: "bg-amber-50",  text: "text-amber-800",  border: "border-amber-200",  badge: "bg-amber-600",   label: "Late + Away" },
  absent:    { bg: "bg-red-50",    text: "text-red-800",    border: "border-red-200",    badge: "bg-red-500",     label: "Absent" },
  holiday:   { bg: "bg-blue-50",   text: "text-blue-800",   border: "border-blue-200",   badge: "bg-blue-500",    label: "Holiday" },
  pending:   { bg: "bg-gray-50",   text: "text-gray-500",   border: "border-gray-200",   badge: "bg-gray-400",    label: "Pending" },
};

function deriveDayStatus(record?: AttendanceRecord, isHol?: boolean): DayStatus {
  if (isHol) return "holiday";
  if (!record) return "pending";
  if (record.status === "absent") return "absent";
  const isLate = !!record.isLate;
  const isAway = record.checkInWithinRange === false;
  if (isLate && isAway) return "late_away";
  if (isLate) return "late";
  if (isAway) return "away";
  return "present";
}

function formatCurrencyShort(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

function toDate(val: any): Date | undefined {
  if (!val) return undefined;
  if (typeof val.toDate === "function") {
    try {
      const d = val.toDate();
      return isValidDate(d) ? d : undefined;
    } catch {
      return undefined;
    }
  }
  if (val instanceof Date) return isValidDate(val) ? val : undefined;
  try {
    const d = new Date(val);
    return isValidDate(d) ? d : undefined;
  } catch {
    return undefined;
  }
}

function isValidDate(d: any): boolean {
  return d instanceof Date && !isNaN(d.getTime());
}

function lateMins(record: AttendanceRecord, shiftStart?: string): number {
  if (!record.isLate || !record.checkInTime) return 0;
  const checkIn = toDate(record.checkInTime);
  if (!checkIn) return 0;
  const [h, m] = (shiftStart || "09:00").split(":").map(Number);
  const expected = new Date(checkIn);
  expected.setHours(h, m, 0, 0);
  return Math.max(0, Math.round((checkIn.getTime() - expected.getTime()) / 60000));
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function AttendanceTracking() {
  const [view, setView] = useState<"monthly" | "daily">("monthly");
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedUser, setSelectedUser] = useState<string>("all");
  const [selectedOffice, setSelectedOffice] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [activeUsers, setActiveUsers] = useState<ApprovedUser[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [dailyCommitmentsData, setDailyCommitmentsData] = useState<DailyCommitment[]>([]);
  const [monthlyCommitmentsData, setMonthlyCommitmentsData] = useState<MonthlyCommitment[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Load data ──
  useEffect(() => {
    loadData();
  }, [currentMonth]);

  // Keep currentMonth in sync when selectedDate crosses month boundaries (daily view prev/next arrows)
  useEffect(() => {
    const monthOfSelected = format(selectedDate, "yyyy-MM");
    const currentMonthStr = format(currentMonth, "yyyy-MM");
    if (monthOfSelected !== currentMonthStr) {
      setCurrentMonth(selectedDate);
    }
  }, [selectedDate]);

  // ── Live office subscription ──
  // Keeps holiday config + office data in sync without a full page refresh.
  // Whenever admin updates an office (e.g. adds/removes a holiday date), the
  // attendance calendar colours and stats update automatically.
  useEffect(() => {
    const officesRef = collection(db, 'offices');
    const q = query(officesRef, where('isActive', '==', true), orderBy('name', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const live = snap.docs.map(d => ({ id: d.id, ...d.data() } as Office));
      setOffices(live);
    }, (err) => {
      console.warn('Office live sync (admin) error:', err);
    });
    return () => unsub();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      const startStr = format(start, "yyyy-MM-dd");
      const endStr = format(end, "yyyy-MM-dd");
      const monthStr = format(currentMonth, "yyyy-MM");

      const [usersData, officesData, att, dc, mc] = await Promise.all([
        getAllActiveUsers(),
        getActiveOffices(), // Also fetched here for initial load; onSnapshot keeps it live
        getAllAttendanceForDateRange(startStr, endStr),
        getDailyCommitments(startStr, endStr),
        getAllMonthlyCommitments(monthStr),
      ]);

      setActiveUsers(usersData);
      // NOTE: offices already managed by onSnapshot listener; set only on first load
      if (officesData.length > 0) setOffices(officesData);
      setAttendanceRecords(att);
      setDailyCommitmentsData(dc);
      setMonthlyCommitmentsData(mc);
    } catch (error) {
      toast.error("Failed to load attendance data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ── Derived data ──
  const filteredUsers = useMemo(() => {
    let users = activeUsers;
    if (selectedOffice !== "all") {
      users = users.filter((u) => u.officeId === selectedOffice);
    }
    if (selectedUser !== "all") {
      users = users.filter((u) => u.userId === selectedUser);
    }
    return users;
  }, [activeUsers, selectedOffice, selectedUser]);

  const officeMap = useMemo(() => new Map(offices.map((o) => [o.id, o])), [offices]);

  const daysInMonth = useMemo(
    () => eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) }),
    [currentMonth]
  );

  const buildDayRecords = (user: ApprovedUser): DayRecord[] => {
    const office = officeMap.get(user.officeId);
    return daysInMonth.map((day) => {
      const dateStr = format(day, "yyyy-MM-dd");
      const hol = isHoliday(day, office?.holidayConfig);
      const record = attendanceRecords.find((r) => r.userId === user.userId && r.date === dateStr);
      const dc = dailyCommitmentsData.find((c) => c.userId === user.userId && c.date === dateStr);
      const isFuture = isBefore(startOfDay(new Date()), startOfDay(day));
      return {
        date: dateStr,
        status: isFuture ? "pending" : deriveDayStatus(record, hol.isHoliday),
        attendance: record,
        dailyCommitment: dc,
        holidayReason: hol.reason,
      };
    });
  };

  // Stats — view-aware: daily view shows selected date only; monthly shows full month
  const stats = useMemo(() => {
    let presentCount = 0, absentCount = 0, lateOnlyCount = 0, awayOnlyCount = 0, lateAwayCount = 0;
    const today = startOfDay(new Date());
    const pastDays = daysInMonth.filter((d) => !isBefore(today, startOfDay(d)));
    const targetDateStr = format(selectedDate, "yyyy-MM-dd");

    filteredUsers.forEach((user) => {
      const allRecords = buildDayRecords(user);
      const records = view === "daily"
        ? allRecords.filter((r) => r.date === targetDateStr)
        : allRecords;
      records.forEach((r) => {
        if (r.status === "present")     presentCount++;
        else if (r.status === "absent")    absentCount++;
        else if (r.status === "late")      lateOnlyCount++;
        else if (r.status === "away")      awayOnlyCount++;
        else if (r.status === "late_away") lateAwayCount++;
      });
    });

    const attended = presentCount + lateOnlyCount + awayOnlyCount + lateAwayCount;
    const total = attended + absentCount;
    return {
      workingDaysOrUsers: selectedUser === "all" ? filteredUsers.length : (view === "daily" ? 1 : pastDays.length),
      workingLabel: selectedUser === "all" ? "Executives" : "Working",
      present: presentCount,
      absent: absentCount,
      late: lateOnlyCount + lateAwayCount,    // All late (incl. late+away)
      away: awayOnlyCount + lateAwayCount,    // All away (incl. late+away)
      lateAway: lateAwayCount,                // Specifically both late AND away
      rate: total > 0 ? (attended / total) * 100 : 0,
    };
  }, [view, selectedDate, filteredUsers, daysInMonth, attendanceRecords, dailyCommitmentsData, offices, selectedUser]);

  // Calendar modifiers
  const calendarModifiers = useMemo(() => {
    const m: Record<string, Date[]> = { present: [], absent: [], late: [], away: [], lateAway: [], holiday: [] };
    filteredUsers.forEach((user) => {
      buildDayRecords(user).forEach((r) => {
        const d = new Date(r.date + "T00:00:00");
        if (r.status === "present") m.present.push(d);
        else if (r.status === "absent") m.absent.push(d);
        else if (r.status === "late") m.late.push(d);
        else if (r.status === "away") m.away.push(d);
        else if (r.status === "late_away") m.lateAway.push(d);
        else if (r.status === "holiday") m.holiday.push(d);
      });
    });
    return m;
  }, [filteredUsers, daysInMonth, attendanceRecords, offices]);

  // Daily view cards
  const dailyCards = useMemo(() => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return filteredUsers
      .map((user) => {
        const office = officeMap.get(user.officeId);
        const hol = isHoliday(selectedDate, office?.holidayConfig);
        const record = attendanceRecords.find((r) => r.userId === user.userId && r.date === dateStr);
        const dc = dailyCommitmentsData.find((c) => c.userId === user.userId && c.date === dateStr);
        return { user, record, dc, status: deriveDayStatus(record, hol.isHoliday) as DayStatus, holidayReason: hol.reason, office };
      })
      .filter((c) => selectedStatus === "all" || c.status === selectedStatus)
      .sort((a, b) => {
        const order: Record<DayStatus, number> = { late_away: 0, late: 1, away: 2, present: 3, absent: 4, holiday: 5, pending: 6 };
        return (order[a.status] ?? 9) - (order[b.status] ?? 9);
      });
  }, [selectedDate, filteredUsers, attendanceRecords, dailyCommitmentsData, offices]);

  // Monthly user cards (single user selected)
  const monthlyUserCards = useMemo(() => {
    if (selectedUser === "all") return [];
    const user = activeUsers.find((u) => u.userId === selectedUser);
    if (!user) return [];
    return buildDayRecords(user).filter((r) => {
      const d = new Date(r.date + "T00:00:00");
      return !isBefore(startOfDay(new Date()), startOfDay(d)) || isSameDay(d, new Date());
    });
  }, [selectedUser, activeUsers, daysInMonth, attendanceRecords, dailyCommitmentsData, offices]);

  const selectedUserMC = useMemo(
    () => (selectedUser === "all" ? null : monthlyCommitmentsData.find((c) => c.userId === selectedUser) ?? null),
    [selectedUser, monthlyCommitmentsData]
  );

  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  // ── Export ──
  const handleExport = () => {
    try {
      const wb = XLSX.utils.book_new();
      const usersToExport = filteredUsers;

      usersToExport.forEach((user, index) => {
        const records = buildDayRecords(user);
        const mc = monthlyCommitmentsData.find((c) => c.userId === user.userId);
        const office = officeMap.get(user.officeId);

        const rows = records.map((r) => {
          const att = r.attendance;
          const dc = r.dailyCommitment;
          const ci = att ? toDate(att.checkInTime) : undefined;
          const co = att ? toDate(att.checkOutTime) : undefined;
          return {
            Date: r.date,
            Day: format(new Date(r.date + "T00:00:00"), "EEEE"),
            Status: STATUS_STYLES[r.status].label,
            "Check-in": ci ? format(ci, "hh:mm a") : "-",
            "Check-out": co ? format(co, "hh:mm a") : "-",
            "Late (min)": att?.isLate ? lateMins(att, office?.attendanceSettings?.shiftStartTime) : "-",
            "Distance (m)": att?.checkInDistance != null ? Math.round(att.checkInDistance) : "-",
            "Within Range": att ? (att.checkInWithinRange ? "Yes" : "No") : "-",
            Latitude: att?.checkInLocation?.latitude ?? "-",
            Longitude: att?.checkInLocation?.longitude ?? "-",
            "Late Reason": att?.lateReason ?? "-",
            "Location Reason": att?.locationReason ?? "-",
            "Login Files": dc?.loginFiles ?? "-",
            "Login Amt": dc?.loginAmount ?? "-",
            "Disb Files": dc?.disbursementFiles ?? "-",
            "Disb Amt": dc?.disbursementAmount ?? "-",
          };
        });

        if (mc) {
          rows.push({
            Date: "Monthly Target" as any,
            Day: "",
            Status: "",
            "Check-in": "",
            "Check-out": "",
            "Late (min)": "" as any,
            "Distance (m)": "" as any,
            "Within Range": "",
            Latitude: "" as any,
            Longitude: "" as any,
            "Late Reason": "",
            "Location Reason": "",
            "Login Files": mc.loginFiles as any,
            "Login Amt": mc.loginAmount as any,
            "Disb Files": mc.disbursementFiles as any,
            "Disb Amt": mc.disbursementAmount as any,
          });
        }

        // Create unique sheet name - sanitize and ensure uniqueness
        const sanitizedName = user.name.slice(0, 25).replace(/[\\\/\?\*\[\]:]/g, "_").trim();
        let sheetName = sanitizedName;
        let counter = 1;
        
        // If sheet name already exists, append counter to make it unique (Excel sheets max 31 chars)
        while (wb.SheetNames.includes(sheetName)) {
          sheetName = `${sanitizedName.slice(0, 20)}_${counter}`;
          counter++;
        }
        
        // Ensure sheet name doesn't exceed Excel's 31 character limit
        sheetName = sheetName.slice(0, 31);
        
        const ws = XLSX.utils.json_to_sheet(rows);
        ws["!cols"] = Object.keys(rows[0] || {}).map(() => ({ wch: 16 }));
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      if (wb.SheetNames.length === 0) {
        toast.error("No data to export");
        return;
      }

      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Attendance_${format(currentMonth, "MMM_yyyy")}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Exported to Excel");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export");
    }
  };

  // ── AttendanceCard ──
  const AttendanceCard = ({
    record,
    dc,
    status,
    user,
    date,
    holidayReason,
    showUser = true,
  }: {
    record?: AttendanceRecord;
    dc?: DailyCommitment;
    status: DayStatus;
    user?: ApprovedUser;
    date: string;
    holidayReason?: string;
    showUser?: boolean;
  }) => {
    const style = STATUS_STYLES[status];
    const checkIn = record ? toDate(record.checkInTime) : undefined;
    const checkOut = record ? toDate(record.checkOutTime) : undefined;
    const office = user ? officeMap.get(user.officeId) : undefined;
    const minsLate = record ? lateMins(record, office?.attendanceSettings?.shiftStartTime) : 0;

    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn("rounded-xl border p-3 sm:p-4 transition-shadow hover:shadow-md", style.bg, style.border)}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            {showUser && user && (
              <div className={cn("h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0", style.badge)}>
                {user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
              </div>
            )}
            <div className="min-w-0">
              {showUser && user && <p className="text-sm font-semibold truncate">{user.name}</p>}
              <p className="text-[11px] text-muted-foreground">
                {format(new Date(date + "T00:00:00"), "EEE, dd MMM yyyy")}
              </p>
            </div>
          </div>
          <Badge className={cn("text-[10px] text-white shrink-0", style.badge)}>{style.label}</Badge>
        </div>

        {/* Holiday */}
        {status === "holiday" && holidayReason && (
          <div className="flex items-center gap-1.5 text-xs text-blue-700 mb-2">
            <PartyPopper className="h-3.5 w-3.5" /> {holidayReason}
          </div>
        )}

        {/* Check-in details - Compact view */}
        {record && status !== "absent" && status !== "holiday" && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-3 text-[11px] flex-wrap">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium">{checkIn ? format(checkIn, "hh:mm a") : "-"}</span>
                <span className="text-muted-foreground">→</span>
                <span className="font-medium">{checkOut ? format(checkOut, "hh:mm a") : "-"}</span>
              </div>
              {record.isLate && (
                <div className="flex items-center gap-1 text-yellow-700 font-medium">
                  ⏰ Late {minsLate}m
                </div>
              )}
              {record.checkInDistance != null && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3 text-muted-foreground" />
                  <span className={cn("font-medium", !record.checkInWithinRange && "text-orange-700")}>
                    {formatDistanceMeters(record.checkInDistance)}
                  </span>
                  {!record.checkInWithinRange && <span className="text-orange-700 text-[10px]">(Away)</span>}
                </div>
              )}
            </div>
            {record.checkInLocation && (
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <a
                  href={`https://www.google.com/maps?q=${record.checkInLocation.latitude},${record.checkInLocation.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-blue-600"
                >
                  📍 {record.checkInLocation.latitude.toFixed(4)}, {record.checkInLocation.longitude.toFixed(4)}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Reasons */}
        {(record?.lateReason || record?.locationReason) && (
          <div className="mt-1.5 space-y-0.5 text-[10px]">
            {record.lateReason && (
              <p className="text-yellow-700"><span className="font-medium">Late:</span> {record.lateReason}</p>
            )}
            {record.locationReason && (
              <p className="text-orange-700"><span className="font-medium">Location:</span> {record.locationReason}</p>
            )}
          </div>
        )}

        {/* Commitment */}
        {dc && (
          <div className="mt-2 pt-2 border-t border-dashed grid grid-cols-2 gap-1.5 text-[11px]">
            <div className="flex items-center gap-1">
              <Target className="h-3 w-3 text-blue-500" />
              <span className="text-muted-foreground">Login:</span>
              <span className="font-medium">{dc.loginFiles}F / {formatCurrencyShort(dc.loginAmount)}</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="text-muted-foreground">Disb:</span>
              <span className="font-medium">{dc.disbursementFiles}F / {formatCurrencyShort(dc.disbursementAmount)}</span>
            </div>
          </div>
        )}

        {/* Absent reason */}
        {record?.status === "absent" && record.absentReason && (
          <div className="mt-1.5 text-[11px] text-red-700">
            <span className="font-medium">Reason:</span>{" "}
            {record.absentReason === "other" ? record.absentRemarks || "Other" : record.absentReason.replace(/_/g, " ")}
          </div>
        )}
      </motion.div>
    );
  };

  // ── RENDER ──
  const [mainTab, setMainTab] = useState<"attendance" | "commitment">("attendance");
  const [commitView, setCommitView] = useState<"daily" | "monthly">("daily");
  const [editingCommitment, setEditingCommitment] = useState<string | null>(null); // userId
  const [editForm, setEditForm] = useState({ loginFiles: 0, loginAmount: 0, disbursementFiles: 0, disbursementAmount: 0 });
  const [saving, setSaving] = useState(false);

  // Commitment - Daily cards (filtered users for selected date)
  const commitDailyCards = useMemo(() => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return filteredUsers.map((user) => {
      const dc = dailyCommitmentsData.find((c) => c.userId === user.userId && c.date === dateStr);
      return { user, dc };
    });
  }, [selectedDate, filteredUsers, dailyCommitmentsData]);

  // Commitment - Monthly cards (filtered users for current month)
  const commitMonthlyCards = useMemo(() => {
    return filteredUsers.map((user) => {
      const mc = monthlyCommitmentsData.find((c) => c.userId === user.userId);
      return { user, mc };
    });
  }, [filteredUsers, monthlyCommitmentsData]);

  // Commitment stats - filtered by office/user
  const commitStats = useMemo(() => {
    if (commitView === "daily") {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const filteredUserIds = filteredUsers.map(u => u.userId);
      const dayCommits = dailyCommitmentsData.filter((c) => 
        c.date === dateStr && filteredUserIds.includes(c.userId)
      );
      return {
        loginFiles: dayCommits.reduce((s, c) => s + c.loginFiles, 0),
        loginAmount: dayCommits.reduce((s, c) => s + c.loginAmount, 0),
        disbFiles: dayCommits.reduce((s, c) => s + c.disbursementFiles, 0),
        disbAmount: dayCommits.reduce((s, c) => s + c.disbursementAmount, 0),
      };
    } else {
      const filteredUserIds = filteredUsers.map(u => u.userId);
      const monthCommits = monthlyCommitmentsData.filter((c) => 
        filteredUserIds.includes(c.userId)
      );
      return {
        loginFiles: monthCommits.reduce((s, c) => s + c.loginFiles, 0),
        loginAmount: monthCommits.reduce((s, c) => s + c.loginAmount, 0),
        disbFiles: monthCommits.reduce((s, c) => s + c.disbursementFiles, 0),
        disbAmount: monthCommits.reduce((s, c) => s + c.disbursementAmount, 0),
      };
    }
  }, [commitView, selectedDate, filteredUsers, dailyCommitmentsData, monthlyCommitmentsData]);

  const handleStartEdit = useCallback((userId: string, existing?: { loginFiles: number; loginAmount: number; disbursementFiles: number; disbursementAmount: number }) => {
    setEditingCommitment(userId);
    setEditForm({
      loginFiles: existing?.loginFiles ?? 0,
      loginAmount: existing?.loginAmount ?? 0,
      disbursementFiles: existing?.disbursementFiles ?? 0,
      disbursementAmount: existing?.disbursementAmount ?? 0,
    });
  }, []);

  const handleSaveCommitment = async (userId: string) => {
    setSaving(true);
    try {
      if (commitView === "daily") {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        await saveDailyCommitment(userId, dateStr, editForm);
        toast.success("Daily commitment saved");
      } else {
        const monthStr = format(currentMonth, "yyyy-MM");
        await saveMonthlyCommitment(userId, monthStr, editForm);
        toast.success("Monthly commitment saved");
      }
      setEditingCommitment(null);
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save commitment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserCheck className="h-5 w-5" />
            Attendance & Commitment
          </CardTitle>
          {mainTab === "attendance" && (
            <Button size="sm" variant="outline" onClick={handleExport} className="p-2">
              <Download className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Main Tabs - Attendance / Commitment */}
        <div className="mt-3">
          <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as "attendance" | "commitment")}>
            <TabsList className="grid w-full max-w-[320px] grid-cols-2 bg-green-100/60">
              <TabsTrigger
                value="attendance"
                className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all font-medium"
              >
                <UserCheck className="h-4 w-4 mr-1.5" />
                Attendance
              </TabsTrigger>
              <TabsTrigger
                value="commitment"
                className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all font-medium"
              >
                <Target className="h-4 w-4 mr-1.5" />
                Commitment
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        <AnimatePresence mode="wait">
          {mainTab === "attendance" ? (
            <motion.div
              key="attendance-tab"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="space-y-5"
            >
        {/* Filters */}
        <div className="space-y-3">
          {/* Monthly/Daily Tabs - Full Width */}
          <Tabs value={view} onValueChange={(v) => setView(v as "monthly" | "daily")} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-green-100/60">
              <TabsTrigger value="monthly" className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md">
                Monthly
              </TabsTrigger>
              <TabsTrigger value="daily" className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md">
                Daily
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Filter Dropdowns with Labels */}
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Office</label>
              <Select value={selectedOffice} onValueChange={setSelectedOffice}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Office" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Offices</SelectItem>
                  {offices.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[160px]">
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">User</label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select User" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {(selectedOffice !== "all"
                    ? activeUsers.filter((u) => u.officeId === selectedOffice)
                    : activeUsers
                  ).map((u) => (
                    <SelectItem key={u.userId} value={u.userId}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[140px]">
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Status</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="late">Late</SelectItem>
                  <SelectItem value="away">Away</SelectItem>
                  <SelectItem value="late_away">Late + Away</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="holiday">Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {[
            { label: stats.workingLabel, value: stats.workingDaysOrUsers, color: "text-foreground" },
            { label: "Present", value: stats.present, color: "text-green-600" },
            { label: "Absent", value: stats.absent, color: "text-red-600" },
            { label: "Late", value: stats.late, color: "text-yellow-600" },
            { label: "Away", value: stats.away, color: "text-orange-600" },
            { label: "Late+Away", value: stats.lateAway, color: "text-amber-600" },
          ].map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className="shadow-none">
                <CardContent className="p-3 text-center">
                  <div className={cn("text-xl font-bold", s.color)}>{s.value}</div>
                  <p className="text-[10px] text-muted-foreground">{s.label}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Monthly View */}
        {view === "monthly" && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => {
                  if (!d) return;
                  setSelectedDate(d);
                }}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                className="rounded-md border"
                modifiers={{
                  present: calendarModifiers.present,
                  absent: calendarModifiers.absent,
                  late: calendarModifiers.late,
                  away: calendarModifiers.away,
                  lateAway: calendarModifiers.lateAway,
                  holiday: calendarModifiers.holiday,
                }}
                modifiersStyles={{
                  present: { backgroundColor: "rgb(34 197 94 / 0.2)", color: "rgb(22 163 74)" },
                  absent: { backgroundColor: "rgb(239 68 68 / 0.2)", color: "rgb(220 38 38)" },
                  late: { backgroundColor: "rgb(234 179 8 / 0.2)", color: "rgb(161 98 7)" },
                  away: { backgroundColor: "rgb(249 115 22 / 0.2)", color: "rgb(194 65 12)" },
                  lateAway: { backgroundColor: "rgb(217 119 6 / 0.2)", color: "rgb(146 64 14)" },
                  holiday: { backgroundColor: "rgb(59 130 246 / 0.2)", color: "rgb(37 99 235)" },
                }}
              />
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 justify-center">
              {(["present", "late", "away", "late_away", "absent", "holiday"] as DayStatus[]).map((s) => (
                <div key={s} className="flex items-center gap-1.5">
                  <div className={cn("h-2.5 w-2.5 rounded-full", STATUS_STYLES[s].badge)} />
                  <span className="text-[11px]">{STATUS_STYLES[s].label}</span>
                </div>
              ))}
            </div>

            {/* Monthly target (single user) */}
            {selectedUser !== "all" && selectedUserMC && (
              <Card className="shadow-none border">
                <CardContent className="p-3">
                  <p className="text-xs font-semibold mb-2 flex items-center gap-1.5">
                    <Target className="h-3.5 w-3.5 text-blue-600" /> Monthly Target
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Login:</span>{" "}
                      <span className="font-medium">{selectedUserMC.loginFiles}F / {formatCurrencyShort(selectedUserMC.loginAmount)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Disb:</span>{" "}
                      <span className="font-medium">{selectedUserMC.disbursementFiles}F / {formatCurrencyShort(selectedUserMC.disbursementAmount)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cards */}
            {selectedUser !== "all" ? (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {activeUsers.find((u) => u.userId === selectedUser)?.name ?? "User"} — {format(currentMonth, "MMMM yyyy")}
                </h3>
                {loading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : monthlyUserCards.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No data for this month</p>
                ) : (
                  <div className="grid gap-2">
                    {monthlyUserCards.map((r) => (
                      <AttendanceCard
                        key={r.date}
                        record={r.attendance}
                        dc={r.dailyCommitment}
                        status={r.status}
                        date={r.date}
                        holidayReason={r.holidayReason}
                        user={activeUsers.find((u) => u.userId === selectedUser)}
                        showUser={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {format(selectedDate, "EEEE, dd MMMM yyyy")}
                </h3>
                {dailyCards.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No data</p>
                ) : (
                  <div className="grid gap-2">
                    {dailyCards.map((c) => (
                      <AttendanceCard
                        key={c.user.userId}
                        record={c.record}
                        dc={c.dc}
                        status={c.status}
                        date={format(selectedDate, "yyyy-MM-dd")}
                        holidayReason={c.holidayReason}
                        user={c.user}
                        showUser
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Daily View */}
        {view === "daily" && (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setSelectedDate(new Date(selectedDate.getTime() - 86400000))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <input
                type="date"
                value={format(selectedDate, "yyyy-MM-dd")}
                onChange={(e) => e.target.value && setSelectedDate(new Date(e.target.value + "T00:00:00"))}
                className="border rounded-md px-3 py-1.5 text-sm w-40 text-center"
              />
              <Button variant="ghost" size="icon" onClick={() => setSelectedDate(new Date(selectedDate.getTime() + 86400000))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <h3 className="text-sm font-semibold text-center flex items-center justify-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              {format(selectedDate, "EEEE, dd MMMM yyyy")}
              <Badge variant="secondary" className="text-[10px]">
                {dailyCards.length} users
              </Badge>
            </h3>

            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : dailyCards.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No attendance records</p>
            ) : (
              <div className="grid gap-2">
                {dailyCards.map((c) => (
                  <AttendanceCard
                    key={c.user.userId}
                    record={c.record}
                    dc={c.dc}
                    status={c.status}
                    date={format(selectedDate, "yyyy-MM-dd")}
                    holidayReason={c.holidayReason}
                    user={c.user}
                    showUser
                  />
                ))}
              </div>
            )}
          </div>
        )}
            </motion.div>
          ) : (
            /* ─────────────────────────────────────────────────────────── */
            /*  COMMITMENT TAB                                             */
            /* ─────────────────────────────────────────────────────────── */
            <motion.div
              key="commitment-tab"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="space-y-5"
            >
              {/* Commitment Filters */}
              <div className="space-y-3">
                {/* Daily/Monthly Tabs - Full Width */}
                <Tabs value={commitView} onValueChange={(v) => setCommitView(v as "daily" | "monthly")} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-green-100/60">
                    <TabsTrigger value="daily" className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md">
                      Daily
                    </TabsTrigger>
                    <TabsTrigger value="monthly" className="data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-md">
                      Monthly
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Filter Dropdowns with Labels */}
                <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Office</label>
                    <Select value={selectedOffice} onValueChange={setSelectedOffice}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Office" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Offices</SelectItem>
                        {offices.map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">User</label>
                    <Select value={selectedUser} onValueChange={setSelectedUser}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select User" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        {(selectedOffice !== "all"
                          ? activeUsers.filter((u) => u.officeId === selectedOffice)
                          : activeUsers
                        ).map((u) => (
                          <SelectItem key={u.userId} value={u.userId}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Date / Month Navigator */}
              {commitView === "daily" ? (
                <div className="flex items-center justify-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => setSelectedDate(new Date(selectedDate.getTime() - 86400000))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <input
                    type="date"
                    value={format(selectedDate, "yyyy-MM-dd")}
                    onChange={(e) => e.target.value && setSelectedDate(new Date(e.target.value + "T00:00:00"))}
                    className="border rounded-md px-3 py-1.5 text-sm w-40 text-center"
                  />
                  <Button variant="ghost" size="icon" onClick={() => setSelectedDate(new Date(selectedDate.getTime() + 86400000))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-sm font-semibold w-36 text-center">{format(currentMonth, "MMMM yyyy")}</span>
                  <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              )}

              {/* Commitment Summary Stats - Filtered */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: "Login Files", value: commitStats.loginFiles, icon: FileText, color: "text-blue-600" },
                  { label: "Login Amt", value: formatCurrencyShort(commitStats.loginAmount), icon: DollarSign, color: "text-blue-600" },
                  { label: "Disb Files", value: commitStats.disbFiles, icon: BarChart3, color: "text-green-600" },
                  { label: "Disb Amt", value: formatCurrencyShort(commitStats.disbAmount), icon: TrendingUp, color: "text-green-600" },
                ].map((s, i) => (
                  <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <Card className="shadow-none">
                      <CardContent className="p-3 text-center">
                        <s.icon className={cn("h-4 w-4 mx-auto mb-1", s.color)} />
                        <div className={cn("text-lg font-bold", s.color)}>{s.value}</div>
                        <p className="text-[10px] text-muted-foreground">{s.label}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Commitment User Cards */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-center flex items-center justify-center gap-2">
                  <Target className="h-4 w-4 text-green-600" />
                  {commitView === "daily"
                    ? `Daily Commitments — ${format(selectedDate, "dd MMM yyyy")}`
                    : `Monthly Commitments — ${format(currentMonth, "MMMM yyyy")}`}
                  <Badge variant="secondary" className="text-[10px]">
                    {commitView === "daily" ? commitDailyCards.length : commitMonthlyCards.length} users
                  </Badge>
                </h3>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="grid gap-2">
                    {commitView === "daily"
                      ? commitDailyCards.map(({ user, dc }) => (
                          <CommitmentCard
                            key={user.userId}
                            user={user}
                            loginFiles={dc?.loginFiles ?? 0}
                            loginAmount={dc?.loginAmount ?? 0}
                            disbursementFiles={dc?.disbursementFiles ?? 0}
                            disbursementAmount={dc?.disbursementAmount ?? 0}
                            hasData={!!dc}
                            isEditing={editingCommitment === user.userId}
                            editForm={editForm}
                            setEditForm={setEditForm}
                            onEdit={() => handleStartEdit(user.userId, dc ?? undefined)}
                            onSave={() => handleSaveCommitment(user.userId)}
                            onCancel={() => setEditingCommitment(null)}
                            saving={saving}
                          />
                        ))
                      : commitMonthlyCards.map(({ user, mc }) => (
                          <CommitmentCard
                            key={user.userId}
                            user={user}
                            loginFiles={mc?.loginFiles ?? 0}
                            loginAmount={mc?.loginAmount ?? 0}
                            disbursementFiles={mc?.disbursementFiles ?? 0}
                            disbursementAmount={mc?.disbursementAmount ?? 0}
                            hasData={!!mc}
                            isEditing={editingCommitment === user.userId}
                            editForm={editForm}
                            setEditForm={setEditForm}
                            onEdit={() => handleStartEdit(user.userId, mc ?? undefined)}
                            onSave={() => handleSaveCommitment(user.userId)}
                            onCancel={() => setEditingCommitment(null)}
                            saving={saving}
                          />
                        ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  CommitmentCard                                                      */
/* ------------------------------------------------------------------ */

function CommitmentCard({
  user,
  loginFiles,
  loginAmount,
  disbursementFiles,
  disbursementAmount,
  hasData,
  isEditing,
  editForm,
  setEditForm,
  onEdit,
  onSave,
  onCancel,
  saving,
}: {
  user: ApprovedUser;
  loginFiles: number;
  loginAmount: number;
  disbursementFiles: number;
  disbursementAmount: number;
  hasData: boolean;
  isEditing: boolean;
  editForm: { loginFiles: number; loginAmount: number; disbursementFiles: number; disbursementAmount: number };
  setEditForm: (f: { loginFiles: number; loginAmount: number; disbursementFiles: number; disbursementAmount: number }) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "rounded-xl border p-3 sm:p-4 transition-shadow hover:shadow-md",
        hasData ? "bg-green-50/50 border-green-200" : "bg-gray-50/50 border-gray-200"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className={cn(
            "h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0",
            hasData ? "bg-green-500" : "bg-gray-400"
          )}>
            {user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{user.name}</p>
            <p className="text-[11px] text-muted-foreground">{user.role || "Team Member"}</p>
          </div>
        </div>
        {!isEditing ? (
          <Button size="sm" variant="ghost" onClick={onEdit} className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-100">
            <Edit3 className="h-3.5 w-3.5" />
            <span className="text-xs hidden sm:inline">Edit</span>
          </Button>
        ) : (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={onCancel} className="text-red-500 hover:bg-red-50 p-1.5 h-8 w-8" disabled={saving}>
              <X className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onSave} className="text-green-600 hover:bg-green-50 p-1.5 h-8 w-8" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      {isEditing ? (
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="text-[10px] font-medium text-muted-foreground mb-0.5 block">Login Files</label>
            <Input
              type="number"
              min={0}
              value={editForm.loginFiles}
              onChange={(e) => setEditForm({ ...editForm, loginFiles: parseInt(e.target.value) || 0 })}
              className="h-9 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium text-muted-foreground mb-0.5 block">Login Amount (₹)</label>
            <Input
              type="number"
              min={0}
              value={editForm.loginAmount}
              onChange={(e) => setEditForm({ ...editForm, loginAmount: parseInt(e.target.value) || 0 })}
              className="h-9 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium text-muted-foreground mb-0.5 block">Disb Files</label>
            <Input
              type="number"
              min={0}
              value={editForm.disbursementFiles}
              onChange={(e) => setEditForm({ ...editForm, disbursementFiles: parseInt(e.target.value) || 0 })}
              className="h-9 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium text-muted-foreground mb-0.5 block">Disb Amount (₹)</label>
            <Input
              type="number"
              min={0}
              value={editForm.disbursementAmount}
              onChange={(e) => setEditForm({ ...editForm, disbursementAmount: parseInt(e.target.value) || 0 })}
              className="h-9 text-sm"
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <div className="flex items-center gap-1.5 bg-white/70 rounded-lg p-2 border border-blue-100">
            <FileText className="h-3.5 w-3.5 text-blue-500 shrink-0" />
            <div>
              <span className="text-muted-foreground">Login:</span>{" "}
              <span className="font-semibold">{loginFiles}F / {formatCurrencyShort(loginAmount)}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-white/70 rounded-lg p-2 border border-green-100">
            <TrendingUp className="h-3.5 w-3.5 text-green-500 shrink-0" />
            <div>
              <span className="text-muted-foreground">Disb:</span>{" "}
              <span className="font-semibold">{disbursementFiles}F / {formatCurrencyShort(disbursementAmount)}</span>
            </div>
          </div>
        </div>
      )}

      {/* No data indicator */}
      {!hasData && !isEditing && (
        <p className="text-[10px] text-muted-foreground mt-1.5 italic">No commitment set</p>
      )}
    </motion.div>
  );
}
